if (typeof customElements.get('bundle-builder') === 'undefined') {
  const MAX_AUTO_LOADS = 10;

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.items = new Map();
      this.storageKey = `bundle-builder:${this.dataset.collection}`;
    }

    connectedCallback() {
      this.sizes = this.dataset.sizes
        .split(',')
        .map((size) => parseInt(size, 10))
        .filter((size) => size > 0)
        .sort((a, b) => a - b);
      this.maxSize = parseInt(this.dataset.maxSize, 10) || this.sizes[this.sizes.length - 1] || 24;

      this.grid = this.querySelector('[data-bundle-grid]');
      this.slotsHolder = this.querySelector('[data-bundle-slots]');
      this.countText = this.querySelector('[data-bundle-count-text]');
      this.progress = this.querySelector('[data-bundle-progress]');
      this.progressFill = this.querySelector('[data-bundle-progress-fill]');
      this.scaleMarks = this.querySelectorAll('[data-bundle-scale-mark]');
      this.submit = this.querySelector('[data-bundle-submit]');
      this.submitLabel = this.querySelector('[data-bundle-submit-label]');
      this.totalText = this.querySelector('[data-bundle-total]');
      this.message = this.querySelector('[data-bundle-message]');
      this.tray = this.querySelector('[data-bundle-tray]');
      this.toggle = this.querySelector('[data-bundle-toggle]');
      this.toggleLabel = this.querySelector('[data-bundle-toggle-label]');
      this.loadMoreButton = this.querySelector('[data-bundle-load-more]');
      this.sentinel = this.querySelector('[data-bundle-sentinel]');
      this.loadingEl = this.querySelector('[data-bundle-loading]');

      this.emptyEl = this.querySelector('[data-bundle-empty]');

      const activeCategory = this.querySelector('.bundle-category--active');
      this.streams = this.parseTabUrls(activeCategory);
      this.streamsKey = this.tabKey(activeCategory);
      this.tabTags = this.parseTabTags(activeCategory);
      this.tabPrice = this.parseTabPrice(activeCategory);
      this.renderedIds = new Set();
      this.loadingPage = false;

      this.perPage = parseInt(this.dataset.perPage, 10) || 50;
      this.autoLoads = 0;

      const reloadOnMount = this.tabIsFiltered();

      if (!reloadOnMount && this.streams[0]) {
        this.streams[0].page = 1;
        this.streams[0].totalPages = this.loadMoreButton
          ? parseInt(this.loadMoreButton.dataset.totalPages, 10) || 1
          : 1;
      }

      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        if (card.dataset.productId) this.renderedIds.add(card.dataset.productId);
        this.bindCard(card);
      });

      this.querySelectorAll('.bundle-category').forEach((button) => {
        button.addEventListener('click', () => this.setCategory(button));
      });

      if (this.toggle) {
        this.toggle.addEventListener('click', () => this.toggleTray());
      }

      if (this.submit) {
        this.submit.addEventListener('click', () => this.addToCart());
      }

      this.setupInfiniteScroll();

      this.restore();
      this.render();

      if (reloadOnMount) {
        this.loadChunk(true);
      } else {
        this.renderEmptyState();
      }
    }

    setupInfiniteScroll() {
      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', () => {
          this.autoLoads = 0;
          this.loadNextPage();
        });
      }

      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.maybeLoadMore();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if (!this.sentinel || typeof IntersectionObserver === 'undefined') {
        return;
      }

      if (this.loadMoreButton) {
        this.loadMoreButton.closest('.bundle-builder__load-more').hidden = true;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage();
          }
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    }

    bindCard(card) {
      const add = card.querySelector('[data-bundle-add]');
      const increase = card.querySelector('[data-bundle-increase]');
      const decrease = card.querySelector('[data-bundle-decrease]');
      const variants = card.querySelector('[data-bundle-variants]');

      if (add) {
        add.addEventListener('click', () => this.changeQuantity(card, 1));
      }
      if (increase) {
        increase.addEventListener('click', () => this.changeQuantity(card, 1));
      }
      if (decrease) {
        decrease.addEventListener('click', () => this.changeQuantity(card, -1));
      }
      if (variants) {
        variants.addEventListener('change', () => {
          const price = card.querySelector('[data-bundle-card-price]');
          const option = this.selectedOption(card);
          if (price && option) {
            price.textContent = option.dataset.priceFormatted;
          }
          this.render();
        });
      }
    }

    selectedOption(card) {
      const select = card.querySelector('[data-bundle-variants]');
      if (!select) return null;
      return select.options[select.selectedIndex] || null;
    }

    changeQuantity(card, delta) {
      const option = this.selectedOption(card);
      if (!option) return;

      const variantId = option.value;
      const current = this.items.has(variantId) ? this.items.get(variantId).qty : 0;
      const next = current + delta;

      if (delta > 0 && this.count() >= this.maxSize) {
        this.setMessage(this.dataset.localeFull);
        return;
      }

      const max = parseInt(option.dataset.max, 10) || 0;
      if (delta > 0 && max > 0 && next > max) return;

      this.setQuantity(variantId, next, card, option);
    }

    setQuantity(variantId, quantity, card, option) {
      if (quantity <= 0) {
        this.items.delete(variantId);
      } else if (this.items.has(variantId)) {
        this.items.get(variantId).qty = quantity;
      } else {
        this.items.set(variantId, {
          qty: quantity,
          productId: card ? card.dataset.productId : '',
          title: card ? card.dataset.productTitle : '',
          variantTitle: option ? option.dataset.variantTitle : '',
          price: option ? parseInt(option.dataset.price, 10) : 0,
          image: option ? option.dataset.image : '',
          max: option ? parseInt(option.dataset.max, 10) || 0 : 0
        });
      }

      this.setMessage('');
      this.persist();
      this.render();
    }

    count() {
      let total = 0;
      this.items.forEach((item) => {
        total += item.qty;
      });
      return total;
    }

    total() {
      let total = 0;
      this.items.forEach((item) => {
        total += item.qty * item.price;
      });
      return total;
    }

    nextSize(count) {
      const next = this.sizes.find((size) => size >= count);
      return next || this.maxSize;
    }

    isValidSize(count) {
      return count > 0 && this.sizes.indexOf(count) !== -1;
    }

    setCategory(button) {
      const streams = this.parseTabUrls(button);
      const key = this.tabKey(button);

      this.querySelectorAll('.bundle-category').forEach((other) => {
        const active = other === button;
        other.classList.toggle('bundle-category--active', active);
        other.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (key === this.streamsKey) return;

      this.streams = streams;
      this.streamsKey = key;
      this.tabTags = this.parseTabTags(button);
      this.tabPrice = this.parseTabPrice(button);
      this.autoLoads = 0;
      this.loadChunk(true);

      const categories = this.querySelector('.bundle-builder__categories');
      if (categories) {
        categories.scrollIntoView({ block: 'start' });
      }
    }

    toggleTray() {
      const open = this.tray.classList.toggle('bundle-tray--open');
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this.toggleLabel) {
        this.toggleLabel.textContent = open ? this.dataset.localeHide : this.dataset.localeShow;
      }
    }

    render() {
      const count = this.count();
      const full = count >= this.maxSize;
      const next = this.nextSize(count);
      const valid = this.isValidSize(count);

      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        const option = this.selectedOption(card);
        if (!option) return;

        const quantity = this.items.has(option.value) ? this.items.get(option.value).qty : 0;
        const add = card.querySelector('[data-bundle-add]');
        const stepper = card.querySelector('[data-bundle-stepper]');
        const value = card.querySelector('[data-bundle-quantity]');
        const increase = card.querySelector('[data-bundle-increase]');

        card.classList.toggle('bundle-card--selected', quantity > 0);

        if (add) {
          add.hidden = quantity > 0;
          add.disabled = full;
        }
        if (stepper) {
          stepper.hidden = quantity === 0;
        }
        if (value) {
          value.textContent = quantity;
        }
        if (increase) {
          const max = parseInt(option.dataset.max, 10) || 0;
          increase.disabled = full || (max > 0 && quantity >= max);
        }
      });

      if (this.countText) {
        this.countText.textContent = this.dataset.localeProgress
          .replace('[count]', count)
          .replace('[total]', this.maxSize);
      }

      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', count);
        this.progress.setAttribute('aria-valuemax', this.maxSize);
      }
      if (this.progressFill) {
        this.progressFill.style.width = `${Math.min(100, (count / this.maxSize) * 100)}%`;
      }
      if (this.scaleMarks) {
        this.scaleMarks.forEach((mark) => {
          const size = parseInt(mark.dataset.size, 10);
          mark.classList.toggle('bundle-progress__scale-mark--reached', count === size);
        });
      }

      if (this.submit) {
        this.submit.disabled = !valid;
      }
      if (this.submitLabel) {
        if (valid) {
          this.submitLabel.textContent = this.dataset.localeSubmit;
        } else {
          this.submitLabel.textContent = this.dataset.localeToNext
            .replace('[count]', next - count)
            .replace('[total]', next);
        }
      }
      if (this.totalText) {
        this.totalText.textContent = count > 0 ? this._formatMoney(this.total(), this.dataset.moneyFormat) : '';
      }

      if (this.message && !this.message.textContent) {
        if (valid) {
          this.setMessage(this.dataset.localeComplete);
        } else if (count > 0) {
          this.setMessage(this.dataset.localeRemaining.replace('[count]', next - count));
        }
      }

      this.renderSlots();
    }

    renderSlots() {
      if (!this.slotsHolder) return;

      const fragment = document.createDocumentFragment();
      const units = [];

      this.items.forEach((item, variantId) => {
        for (let index = 0; index < item.qty; index += 1) {
          units.push({ variantId, item });
        }
      });

      const slotCount = Math.max(this.nextSize(units.length), units.length);
      for (let index = 0; index < slotCount; index += 1) {
        const slot = document.createElement('div');
        slot.classList.add('bundle-slot');

        const unit = units[index];
        if (unit) {
          if (unit.item.image) {
            const image = document.createElement('img');
            image.classList.add('bundle-slot__image');
            image.src = unit.item.image;
            image.alt = unit.item.title;
            image.width = 64;
            image.height = 84;
            image.loading = 'lazy';
            slot.appendChild(image);
          }

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.classList.add('bundle-slot__remove');
          remove.innerHTML = '&times;';
          remove.setAttribute('aria-label', this.dataset.localeRemove.replace('[title]', unit.item.title));
          remove.addEventListener('click', () => {
            const current = this.items.get(unit.variantId);
            if (!current) return;
            this.setQuantity(unit.variantId, current.qty - 1);
          });
          slot.appendChild(remove);
        } else {
          slot.classList.add('bundle-slot--empty');
          slot.setAttribute('aria-label', this.dataset.localeEmptySlot);
        }

        fragment.appendChild(slot);
      }

      this.slotsHolder.innerHTML = '';
      this.slotsHolder.appendChild(fragment);
    }

    setMessage(text, isError) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.classList.toggle('bundle-bar__message--error', Boolean(isError));
    }

    parseTabUrls(button) {
      const raw = (button && button.dataset.tagUrls) || window.location.pathname;
      const urls = raw
        .split('|')
        .map((url) => url.trim())
        .filter(Boolean);
      return (urls.length ? urls : [window.location.pathname]).map((url) => ({
        url,
        page: 0,
        totalPages: null
      }));
    }

    parseTabTags(button) {
      const raw = (button && button.dataset.tagNames) || '';
      return raw
        .split('|')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }

    parseTabPrice(button) {
      const read = (value) => {
        if (!value) return null;
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      };

      return {
        min: read(button && button.dataset.priceMin),
        max: read(button && button.dataset.priceMax)
      };
    }

    hasPriceRange() {
      return Boolean(this.tabPrice && (this.tabPrice.min !== null || this.tabPrice.max !== null));
    }

    tabIsFiltered() {
      return this.tabTags.length > 0 || this.hasPriceRange();
    }

    tabKey(button) {
      const price = this.parseTabPrice(button);
      const urls = this.parseTabUrls(button)
        .map((stream) => stream.url)
        .join('|');
      return `${urls}#${price.min === null ? '' : price.min}-${price.max === null ? '' : price.max}`;
    }

    cardMatchesTab(card) {
      if (this.tabTags.length) {
        const cardTags = (card.dataset.tags || '').split('|').filter(Boolean);
        if (!this.tabTags.some((tag) => cardTags.includes(tag))) return false;
      }

      if (!this.hasPriceRange()) return true;

      const price = parseInt(card.dataset.price, 10);
      if (Number.isNaN(price)) return true;
      if (this.tabPrice.min !== null && price < this.tabPrice.min) return false;
      if (this.tabPrice.max !== null && price > this.tabPrice.max) return false;

      return true;
    }

    renderEmptyState() {
      if (!this.emptyEl) return;
      const hasCards = Boolean(this.grid.querySelector('[data-bundle-card]'));
      const hasStaticEmpty = Boolean(this.grid.querySelector('.bundle-grid__empty'));
      this.emptyEl.hidden = hasCards || hasStaticEmpty || !this.streamsExhausted();
    }

    nextStream() {
      return this.streams.find((stream) => stream.totalPages === null || stream.page < stream.totalPages);
    }

    streamsExhausted() {
      return !this.nextStream();
    }

    loadNextPage() {
      if (this.loadingPage || this.streamsExhausted()) return;
      this.loadChunk(false);
    }

    async loadChunk(replace) {
      if (this.loadingPage) return;

      if (replace) {
        this.grid.innerHTML = '';
        this.renderedIds.clear();
        if (this.emptyEl) this.emptyEl.hidden = true;
      }

      const stream = this.nextStream();
      if (!stream) return;

      const page = stream.page + 1;

      this.loadingPage = true;
      if (this.loadingEl) this.loadingEl.hidden = false;

      try {
        const response = await fetch(
          `${stream.url}?page=${page}&section_id=${this.dataset.sectionId}`
        );
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');

        const cardEls = parsed.querySelectorAll('[data-bundle-card]');
        let matched = 0;
        cardEls.forEach((card) => {
          if (!this.cardMatchesTab(card)) return;
          matched += 1;

          const id = card.dataset.productId;
          if (id && this.renderedIds.has(id)) return;
          if (id) this.renderedIds.add(id);

          const imported = document.importNode(card, true);
          this.grid.appendChild(imported);
          this.bindCard(imported);
        });

        const moreButton = parsed.querySelector('[data-bundle-load-more]');
        stream.totalPages = moreButton ? parseInt(moreButton.dataset.totalPages, 10) || 1 : 1;
        stream.page = page;

        if (this.tabTags.length && !this.hasPriceRange() && matched < cardEls.length) {
          stream.totalPages = stream.page;
        }

        this.renderEmptyState();
        this.renderLoadMore();
        this.render();
      } catch (error) {
        this.setMessage(this.dataset.localeError, true);
      } finally {
        this.loadingPage = false;
        if (this.loadingEl) this.loadingEl.hidden = true;
        this.maybeLoadMore();
      }
    }

    maybeLoadMore() {
      if (this.loadingPage || this.streamsExhausted()) return;

      const shown = this.grid.querySelectorAll('[data-bundle-card]').length;

      if (this.tabIsFiltered() && shown < this.perPage && this.autoLoads < MAX_AUTO_LOADS) {
        this.autoLoads += 1;
        this.loadNextPage();
        return;
      }

      if (!this.sentinel) return;

      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const rect = this.sentinel.getBoundingClientRect();
      if (rect.top <= viewport + 400) {
        this.loadNextPage();
      }
    }

    renderLoadMore() {
      if (!this.loadMoreButton) return;
      const holder = this.loadMoreButton.closest('.bundle-builder__load-more');
      if (!holder) return;
      holder.hidden = this.streamsExhausted() || !this.observer || this.autoLoads < MAX_AUTO_LOADS;
    }

    async addToCart() {
      if (!this.isValidSize(this.count())) return;

      const bundleId = `bdl-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
      const code = bundleId.slice(-4).toUpperCase();
      const label = this.dataset.propertyLabel;

      const items = [];
      this.items.forEach((item, variantId) => {
        const properties = {
          _bundle_id: bundleId,
          _bundle_sizes: this.dataset.sizes
        };
        if (label) {
          properties[label] = code;
        }
        items.push({ id: Number(variantId), quantity: item.qty, properties });
      });

      this.submit.classList.add('processing');
      this.submit.disabled = true;

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          throw new Error('cart-add-failed');
        }

        this.items.clear();
        this.clearStorage();
        this.render();

        if (typeof window.refreshCart === 'function') {
          window.refreshCart();
        } else {
          window.location.assign(this.dataset.cartUrl);
        }
      } catch (error) {
        this.setMessage(this.dataset.localeError, true);
      } finally {
        this.submit.classList.remove('processing');
        this.submit.disabled = !this.isValidSize(this.count());
      }
    }

    persist() {
      try {
        window.sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({ items: Array.from(this.items.entries()) })
        );
      } catch (error) {
        return;
      }
    }

    clearStorage() {
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        return;
      }
    }

    restore() {
      let stored = null;
      try {
        stored = JSON.parse(window.sessionStorage.getItem(this.storageKey));
      } catch (error) {
        stored = null;
      }
      if (!stored || !Array.isArray(stored.items)) return;

      stored.items.forEach(([variantId, item]) => {
        if (item && item.qty > 0) {
          this.items.set(String(variantId), item);
        }
      });
    }

    _formatMoney(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
}
